import { ChevronDown, ChevronUp, ChevronsUpDown, Search, XCircle } from "lucide-react";
import React, { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Skeleton } from "../../components/ui/skeleton";
import { StatusBadge } from "../../components/ui/status-badge";
import { adminApi, AdminDeployment } from "../../lib/adminApi";
import { notify } from "../../lib/notify";

type SortField = "createdAt" | "status" | "duration";
type SortOrder = "asc" | "desc";

const CANCELLABLE = new Set(["building", "pending", "deploying"]);

function formatDate(d?: string) {
  return d ? new Date(d).toLocaleString() : "—";
}

function fmtDuration(secs?: number) {
  if (!secs) return "—";
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

function SortIcon({ field, active, order }: { field: string; active: boolean; order: SortOrder }) {
  if (!active) return <ChevronsUpDown className="ml-1 inline h-3 w-3 opacity-40" />;
  return order === "asc"
    ? <ChevronUp className="ml-1 inline h-3 w-3" />
    : <ChevronDown className="ml-1 inline h-3 w-3" />;
}

export default function AdminDeploymentsPage() {
  const [deployments, setDeployments] = useState<AdminDeployment[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const load = (p = page, lim = limit) => {
    setLoading(true);
    adminApi
      .listDeployments({
        page: p,
        limit: lim,
        status: statusFilter || undefined,
        sort: sortField,
        order: sortOrder,
        createdAfter: dateFrom ? new Date(dateFrom).toISOString() : undefined,
        createdBefore: dateTo ? new Date(dateTo + "T23:59:59").toISOString() : undefined,
        enrich: true,
      })
      .then((res) => {
        setDeployments(res.deployments);
        setTotal(res.total);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(1); setPage(1); }, [statusFilter, sortField, sortOrder, dateFrom, dateTo, limit, search]); // eslint-disable-line

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(o => o === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const handleCancel = async (d: AdminDeployment) => {
    const id = d._id ?? d.id;
    if (!id) return;
    setCancellingId(id);
    try {
      await adminApi.cancelDeployment(id);
      notify.success("Deployment cancelled");
      setDeployments(prev => prev.map(dep =>
        (dep._id ?? dep.id) === id ? { ...dep, status: "cancelled" } : dep
      ));
    } catch (err: any) {
      notify.error(err?.message ?? "Failed to cancel deployment");
    } finally {
      setCancellingId(null);
    }
  };

  // Client-side filter as fallback for search (backend may not support it yet)
  const displayed = search
    ? deployments.filter(d =>
        (d.serviceName ?? "").toLowerCase().includes(search.toLowerCase()) ||
        (d.branch ?? "").toLowerCase().includes(search.toLowerCase())
      )
    : deployments;

  const totalPages = Math.ceil(total / limit);

  const thClass = "px-4 py-3 text-left font-medium cursor-pointer select-none whitespace-nowrap hover:text-foreground transition-colors";

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-h2 tracking-tight">Deployments</h1>
        <p className="text-sm text-muted-foreground mt-0.5">{total} total deployments</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <form onSubmit={handleSearchSubmit} className="relative">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Service or branch…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            className="pl-8 w-60 h-9"
          />
        </form>

        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="h-9 rounded-md bg-background px-3 text-sm"
        >
          <option value="">All statuses</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
          <option value="building">Building</option>
          <option value="pending">Pending</option>
          <option value="deploying">Deploying</option>
          <option value="cancelled">Cancelled</option>
        </select>

        <div className="flex items-center gap-1.5">
          <label className="text-xs text-muted-foreground">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="h-9 rounded-md bg-background px-2 text-sm"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <label className="text-xs text-muted-foreground">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="h-9 rounded-md bg-background px-2 text-sm"
          />
        </div>
        {(dateFrom || dateTo) && (
          <Button size="sm" variant="ghost" className="h-9 text-xs text-muted-foreground"
            onClick={() => { setDateFrom(""); setDateTo(""); }}>
            Clear dates
          </Button>
        )}

        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Rows</span>
          <select
            value={limit}
            onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
            className="h-9 rounded-md bg-background px-2 text-sm"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-md bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/30 text-xs text-muted-foreground">
              <th className="px-4 py-3 text-left font-medium">Service</th>
              <th className={thClass} onClick={() => handleSort("status")}>
                Status <SortIcon field="status" active={sortField === "status"} order={sortOrder} />
              </th>
              <th className="px-4 py-3 text-left font-medium">Triggered by</th>
              <th className="px-4 py-3 text-left font-medium">Branch</th>
              <th className="px-4 py-3 text-left font-medium">Commit</th>
              <th className={thClass} onClick={() => handleSort("duration")}>
                Duration <SortIcon field="duration" active={sortField === "duration"} order={sortOrder} />
              </th>
              <th className={thClass} onClick={() => handleSort("createdAt")}>
                Date <SortIcon field="createdAt" active={sortField === "createdAt"} order={sortOrder} />
              </th>
              <th className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading
              ? Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 8 }).map((__, j) => (
                      <td key={j} className="px-4 py-3">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              : displayed.map((d) => {
                  const id = d._id ?? d.id ?? "";
                  const cancellable = CANCELLABLE.has(d.status ?? "");
                  return (
                    <tr key={id}
                      className="hover:bg-muted/40 transition-colors cursor-pointer"
                      onClick={() => {
                        const pid = d.projectId;
                        if (pid && id) window.open(`/projects/${pid}/deployments/${id}`, "_blank");
                      }}>
                      <td className="px-4 py-3 font-medium">{d.serviceName ?? d.serviceId ?? "—"}</td>
                      <td className="px-4 py-3">
                        <StatusBadge kind="deployment" status={d.status ?? "unknown"} />
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {d.triggeredByEmail
                          ? <span title={d.triggeredBy}>{d.triggeredByEmail}</span>
                          : d.trigger === "git-push"
                            ? <Badge variant="outline" className="text-xs">git-push</Badge>
                            : (d.trigger ? <Badge variant="outline" className="text-xs">{d.trigger}</Badge> : "—")}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{d.branch ?? "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                        {d.gitCommit ? d.gitCommit.slice(0, 7) : "—"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{fmtDuration(d.duration)}</td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{formatDate(d.createdAt)}</td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        {cancellable && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                title="Cancel deployment"
                                disabled={cancellingId === id}
                              >
                                <XCircle className="h-3.5 w-3.5" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Cancel deployment?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will mark the deployment as cancelled and stop the associated service build. This cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Keep running</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  onClick={() => handleCancel(d)}
                                >
                                  Cancel deployment
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </td>
                    </tr>
                  );
                })}
          </tbody>
        </table>
        {!loading && displayed.length === 0 && (
          <p className="py-10 text-center text-sm text-muted-foreground">No deployments found.</p>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Page {page} of {totalPages} · {total} total</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1}
              onClick={() => { const p = page - 1; setPage(p); load(p); }}>
              Previous
            </Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages}
              onClick={() => { const p = page + 1; setPage(p); load(p); }}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
